import { useEffect, useState } from "react";
import { RefreshCw, Search } from "lucide-react";
import api from "@/lib/api";

const STATUS_STYLE = {
  sent: "border-emerald-400/60 text-emerald-300",
  failed: "border-accent text-accent",
  silenced: "border-white/25 text-white/50",
  blocked: "border-amber-400/60 text-amber-300",
};

export default function AdminEmailLog() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = (q = email) => {
    setBusy(true);
    api.get(`/admin/email-log${q.trim() ? `?email=${encodeURIComponent(q.trim())}` : ""}`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(""); }, []);

  const counts = data?.counts || {};
  const rows = data?.rows || [];

  return (
    <section className="mb-16" data-testid="admin-email-log">
      <p className="overline-label mb-2">Email log — every send attempt, kept 30 days</p>
      <p className="text-sm text-white/45 mb-6 max-w-2xl">
        A member says they never got it? Search their address. <span className="text-emerald-300">Sent</span> means the
        email service accepted it (spam folder is the next stop to check); <span className="text-accent">failed</span> shows the exact error.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative w-full sm:w-80">
          <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
          <input
            data-testid="email-log-search"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search a member's email…"
            className="field !pl-10 !py-2.5 w-full"
          />
        </div>
        <button onClick={() => load()} disabled={busy} className="btn-ghost !py-2.5 disabled:opacity-40" data-testid="email-log-go">
          Search
        </button>
        <button
          onClick={() => { setEmail(""); load(""); }}
          disabled={busy}
          className="btn-ghost !py-2.5 inline-flex items-center gap-2 disabled:opacity-40"
          data-testid="email-log-refresh"
        >
          <RefreshCw size={13} /> Refresh
        </button>
        {data && (
          <p className="font-grotesk text-[10px] uppercase tracking-[0.15em] text-white/40" data-testid="email-log-counts">
            {["sent", "failed", "silenced", "blocked"].map((s) => `${s} ${counts[s] || 0}`).join(" · ")}
          </p>
        )}
      </div>

      <div className="panel max-h-[480px] overflow-y-auto">
        {rows.length === 0 ? (
          <p className="p-6 font-display italic text-2xl text-white/40" data-testid="email-log-empty">
            {email.trim() ? "Nothing sent to that address." : "No email activity yet."}
          </p>
        ) : (
          rows.map((r) => (
            <div key={r.id} className="px-5 py-3 border-b border-white/10 last:border-0 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid={`email-log-row-${r.id}`}>
              <span
                className={`font-grotesk text-[9px] uppercase tracking-[0.15em] border px-2 py-1 ${STATUS_STYLE[r.status] || STATUS_STYLE.silenced}`}
                data-testid={`email-log-status-${r.id}`}
              >
                {r.status}
              </span>
              <p className="font-grotesk text-xs text-white/85 min-w-[200px]">{r.to}</p>
              <p className="text-xs text-white/45 flex-1 truncate" title={r.error || r.subject}>
                {r.status === "failed" && r.error ? r.error : r.subject}
              </p>
              <p className="font-grotesk text-[10px] uppercase tracking-[0.12em] text-white/40">
                {r.created_at ? new Date(r.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}
              </p>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
